import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reserveSession, cancelReservation, getReservations, updateBookingStatus, getMemberBookings, forcePromoteWaitlist } from './api';
import { groupBookingKeys } from './keys';
import { groupSessionKeys } from '../groupSession/keys';
import { groupTicketKeys } from '../groupTicket/keys';
import { ReserveBody } from './types';

export const useReservations = ({ centerId, sessionId, enabled = true }: { centerId: string; sessionId: string; enabled?: boolean }) =>
    useQuery({
        queryKey: groupBookingKeys.reservations(sessionId),
        queryFn: () => getReservations(centerId, sessionId),
        enabled: enabled && !!centerId && !!sessionId,
        select: (data) => data?.data ?? null,
    });

export const useReserveSession = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, sessionId, body }: { centerId: string; sessionId: string; body: ReserveBody }) => reserveSession(centerId, sessionId, body),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupBookingKeys.reservations(v.sessionId) });
            qc.invalidateQueries({ queryKey: groupSessionKeys.detail(v.sessionId) });
            qc.invalidateQueries({ queryKey: groupSessionKeys.all });
            // 예약 추가는 회원 그룹수강권 회차/예약목록도 바꾸므로 함께 무효화
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
            qc.invalidateQueries({ queryKey: groupBookingKeys.all });
            qc.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
        },
    });
};

export const useCancelReservation = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, sessionId, memberId, params }: { centerId: string; sessionId: string; memberId: string; params?: Record<string, string> }) => cancelReservation(centerId, sessionId, memberId, params),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupBookingKeys.reservations(v.sessionId) });
            qc.invalidateQueries({ queryKey: groupSessionKeys.detail(v.sessionId) });
            qc.invalidateQueries({ queryKey: groupSessionKeys.all });
            // 예약 취소는 회원 그룹수강권 회차/예약목록도 바꾸므로 함께 무효화
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
            qc.invalidateQueries({ queryKey: groupBookingKeys.all });
            qc.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
        },
    });
};

export const useMemberBookings = ({ memberId, status, page, limit, enabled = true }: { memberId: string; status?: string; page?: number; limit?: number; enabled?: boolean }) => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (page) params.page = String(page);
    if (limit) params.limit = String(limit);
    return useQuery({
        queryKey: [...groupBookingKeys.all, 'memberBookings', memberId, status, page],
        queryFn: () => getMemberBookings(memberId, params),
        enabled: enabled && !!memberId,
        select: (data) => data?.data ?? null,
    });
};

export const useUpdateBookingStatus = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, bookingId, body, sessionId }: { centerId: string; bookingId: string; body: { status: string }; sessionId: string }) => updateBookingStatus(centerId, bookingId, body),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupBookingKeys.reservations(v.sessionId) });
            qc.invalidateQueries({ queryKey: groupSessionKeys.detail(v.sessionId) });
            // 출석상태 변경은 회원 그룹수강권 회차/예약목록도 바꾸므로 함께 무효화 (vars 에 memberId 없어 broad prefix)
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
            qc.invalidateQueries({ queryKey: groupBookingKeys.all });
            qc.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
        },
    });
};

export const useForcePromoteWaitlist = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, sessionId, memberId }: { centerId: string; sessionId: string; memberId: string }) =>
            forcePromoteWaitlist(centerId, sessionId, memberId),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupBookingKeys.reservations(v.sessionId) });
            qc.invalidateQueries({ queryKey: groupSessionKeys.detail(v.sessionId) });
            qc.invalidateQueries({ queryKey: groupSessionKeys.all });
        },
    });
};
